<template>
  <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" modal append-to="body"
          header="Agendar consulta" :style="{ width: '720px', maxWidth: 'calc(100vw - 24px)' }"
          :draggable="false" :closable="!saving" :close-on-escape="!saving">
    <div v-if="success" class="quick-schedule__success" role="status">
      <i class="bi bi-check-circle text-success" aria-hidden="true"></i>
      <h3>Consulta agendada!</h3>
      <p>{{ schedule.patient.patient_name }}</p>
      <strong>{{ formatDate(form.date) }} às {{ selectedSlot.available_time.slice(0, 5) }}</strong>
      <p class="mt-2">{{ selectedSpecialty?.spec_name }} · {{ schedule.professional?.prof_name }}</p>
      <small>Você pode continuar o atendimento atual.</small>
    </div>
    <form v-else id="attendance-quick-schedule" @submit.prevent="submit">
      <div class="quick-schedule__patient">
        <i class="bi bi-person" aria-hidden="true"></i>
        <div><strong>{{ schedule.patient?.patient_name }}</strong><small>{{ schedule.professional?.prof_name }} · {{ place.place_name }}</small></div>
      </div>
      <p v-if="initializing" role="status" class="text-muted">Carregando opções de agendamento…</p>
      <template v-else>
        <label for="quick-schedule-specialty" class="form-label">Especialidade</label>
        <select id="quick-schedule-specialty" v-model="form.specialtyId" class="form-select mb-4" required
                :disabled="saving || !specialties.length" @change="loadDates">
          <option disabled value="">Selecione a especialidade</option>
          <option v-for="specialty in specialties" :key="specialty.id_spec" :value="specialty.id_spec">{{ specialty.spec_name }}</option>
        </select>
        <div class="quick-schedule__availability">
          <div>
            <label class="form-label" for="quick-schedule-date">Dia da consulta</label>
            <Calendar v-model="calendarDate" input-id="quick-schedule-date" date-format="yy/mm/dd" inline
                      :min-date="minimumDate" :manual-input="false" :disabled="saving || !form.specialtyId"
                      @date-select="loadTimes" @month-change="loadCalendarMonth">
              <template #date="{ date }">
                <span v-if="availabilityForDate(date)" class="quick-schedule__day"
                      :style="{ color: availabilityForDate(date).text_color, backgroundColor: availabilityForDate(date).background_color }"
                      :title="`${availabilityForDate(date).busy_rate}% de ocupação`">{{ date.day }}</span>
                <i v-else>{{ date.day }}</i>
              </template>
            </Calendar>
            <div class="quick-schedule__legend"><BusyRateLegend /></div>
          </div>
          <div>
            <label for="quick-schedule-time" class="form-label">Horário</label>
            <p v-if="loadingDates || loadingTimes" class="text-muted fs-7" role="status">Buscando horários livres…</p>
            <select v-else-if="slots.length" id="quick-schedule-time" class="form-select" size="10" required
                    :disabled="saving" :value="selectedSlot ? slots.indexOf(selectedSlot) : ''"
                    @change="selectedSlot = slots[Number($event.target.value)]">
              <option disabled value="">Selecione</option>
              <option v-for="(slot, index) in slots" :key="index" :value="index">{{ slot.available_time.slice(0, 5) }}</option>
            </select>
            <p v-else class="quick-schedule__empty" role="status">{{ form.date ? 'Nenhum horário livre neste dia. Escolha outra data.' : 'Escolha o dia para ver os horários.' }}</p>
          </div>
        </div>
        <label for="quick-schedule-observation" class="form-label mt-4">Observação <span class="text-muted fw-normal">(opcional)</span></label>
        <textarea id="quick-schedule-observation" v-model.trim="form.observation" class="form-control" rows="2"
                  :disabled="saving" placeholder="Algo importante para a próxima consulta?"></textarea>
        <p v-if="selectedSlot" class="quick-schedule__summary">{{ selectedSpecialty?.spec_name }} · {{ formatDate(form.date) }} às {{ selectedSlot.available_time.slice(0, 5) }}</p>
      </template>
      <div v-if="error" class="alert alert-danger mt-4 mb-0" role="alert">
        {{ error }}
        <button v-if="!specialties.length" type="button" class="btn btn-sm btn-light mt-2" @click="initialize">Tentar novamente</button>
      </div>
    </form>
    <template #footer>
      <button v-if="success" type="button" class="btn btn-primary" @click="$emit('update:visible', false)">Concluir</button>
      <template v-else>
        <button type="button" class="btn btn-light" :disabled="saving" @click="$emit('update:visible', false)">Cancelar</button>
        <button type="submit" form="attendance-quick-schedule" class="btn btn-primary"
                :disabled="initializing || loadingDates || loadingTimes || saving || !selectedSlot">
          {{ saving ? 'Agendando…' : 'Confirmar agendamento' }}
        </button>
      </template>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Calendar from 'primevue/calendar';
import BusyRateLegend from '../../calendar/BusyRateLegend.vue';
import moment from '../../../helpers/date';
import { identityError } from '../../../helpers/patientIdentity';
import { availabilityParams, loadSchedulingSpecialties, loadSchedulingDates, loadSchedulingTimes, createAttendanceSchedule } from '../../../service/attendanceScheduling';

export default {
  name: 'AttendanceScheduleDialog',
  components: { Dialog, Calendar, BusyRateLegend },
  props: { visible: Boolean, schedule: { type: Object, required: true }, place: { type: Object, required: true } },
  emits: ['update:visible', 'scheduled'],
  data() {
    return {
      form: { specialtyId: '', date: '', observation: '' }, specialties: [], slots: [], availableDates: [], selectedSlot: null, calendarRequestId: 0,
      initializing: false, loadingDates: false, loadingTimes: false, saving: false, success: false, error: '', requestId: 0,
    };
  },
  computed: {
    today() { return moment().format('YYYY-MM-DD'); },
    minimumDate() { return moment().startOf('day').toDate(); },
    calendarDate: {
      get() { return this.form.date ? moment(this.form.date, 'YYYY-MM-DD').toDate() : null; },
      set(value) { this.form.date = value ? moment(value).format('YYYY-MM-DD') : ''; },
    },
    selectedSpecialty() { return this.specialties.find(specialty => specialty.id_spec === this.form.specialtyId); },
    params() { return availabilityParams(this.schedule, this.place, this.form.specialtyId); },
  },
  watch: {
    visible(open) {
      if (open) this.initialize();
      else this.requestId++;
    },
  },
  methods: {
    formatDate(value) { return moment(value).format('DD/MM/YYYY'); },
    availabilityForDate(date) {
      const key = moment({ year: date.year, month: date.month, date: date.day }).format('YYYY-MM-DD');
      return this.availableDates.find(day => day.available_date === key);
    },
    async loadCalendarMonth({ year, month }) {
      const requestId = ++this.calendarRequestId;
      this.loadingDates = true;
      try {
        const startDate = moment({ year, month: month - 1, date: 1 }).format('YYYY-MM-DD');
        const dates = await loadSchedulingDates(this.params, startDate);
        if (requestId === this.calendarRequestId) this.availableDates = dates;
      } catch (error) {
        if (requestId === this.calendarRequestId) this.error = identityError(error);
      } finally {
        if (requestId === this.calendarRequestId) this.loadingDates = false;
      }
    },
    async initialize() {
      const requestId = ++this.requestId;
      this.calendarRequestId++;
      this.availableDates = [];
      this.form = { specialtyId: '', date: '', observation: '' };
      this.selectedSlot = null;
      this.slots = [];
      this.specialties = [];
      this.success = false;
      this.error = '';
      this.initializing = true;
      try {
        if (!this.schedule.id_paymeth) throw new Error('A consulta atual não tem forma de pagamento. Agende pela agenda ou peça ajuda à recepção.');
        const specialties = await loadSchedulingSpecialties(this.schedule, this.place);
        if (requestId !== this.requestId) return;
        this.specialties = specialties;
        if (!specialties.length) throw new Error('Nenhuma especialidade disponível neste local. Peça ajuda à recepção.');
        this.form.specialtyId = specialties.find(specialty => specialty.id_spec === this.schedule.id_spec)?.id_spec || specialties[0].id_spec;
        await this.loadDates();
      } catch (error) {
        if (requestId === this.requestId) this.error = error.response ? identityError(error) : error.message;
      } finally {
        this.initializing = false;
      }
    },
    async loadDates() {
      const requestId = ++this.requestId;
      this.calendarRequestId++;
      this.selectedSlot = null;
      this.slots = [];
      this.form.date = '';
      this.error = '';
      this.loadingDates = true;
      this.loadingTimes = false;
      try {
        const dates = await loadSchedulingDates(this.params, this.today);
        if (requestId !== this.requestId) return;
        this.availableDates = dates;
        this.form.date = dates.map(day => day.available_date).filter(day => day >= this.today).sort()[0] || this.today;
        await this.loadTimes();
      } catch (error) {
        if (requestId === this.requestId) this.error = identityError(error);
      } finally {
        this.loadingDates = false;
      }
    },
    async loadTimes() {
      const requestId = ++this.requestId;
      this.selectedSlot = null;
      this.slots = [];
      this.error = '';
      this.loadingTimes = true;
      try {
        if (!moment(this.form.date, 'YYYY-MM-DD', true).isValid() || this.form.date < this.today) return;
        const slots = await loadSchedulingTimes(this.params, this.form.date);
        if (requestId === this.requestId) this.slots = slots;
      } catch (error) {
        if (requestId === this.requestId) this.error = identityError(error);
      } finally {
        if (requestId === this.requestId) this.loadingTimes = false;
      }
    },
    async submit() {
      if (this.saving || this.loadingTimes || this.loadingDates || !this.selectedSlot) return;
      this.saving = true;
      this.error = '';
      try {
        const freshSlots = await loadSchedulingTimes(this.params, this.form.date);
        const available = freshSlots.some(slot => slot.available_time === this.selectedSlot.available_time
          && (slot.id_place_location || null) === (this.selectedSlot.id_place_location || null));
        if (!available) {
          this.slots = freshSlots;
          this.selectedSlot = null;
          this.error = 'Esse horário acabou de ficar indisponível. Escolha outro horário para continuar.';
          return;
        }
        const response = await createAttendanceSchedule(this.schedule, this.place, this.form, this.selectedSlot);
        this.success = true;
        this.$emit('scheduled', response.data);
      } catch (error) {
        this.error = identityError(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.quick-schedule__patient { display: flex; align-items: center; gap: 10px; padding: 12px; background: #f7f5f9; border-radius: 10px; }
.quick-schedule__patient > i { font-size: 22px; color: #780f5a; }
.quick-schedule__patient strong, .quick-schedule__patient small { display: block; }
.quick-schedule__patient small { font-size: 12px; color: #706b79; }
.quick-schedule__patient { margin-bottom: 20px; }
.quick-schedule__availability { display: grid; grid-template-columns: minmax(0, 1fr) 145px; gap: 20px; }
.quick-schedule__availability :deep(.p-calendar), .quick-schedule__availability :deep(.p-datepicker) { width: 100%; }
.quick-schedule__availability :deep(.p-datepicker) { border: 0; padding: 0; }
.quick-schedule__availability :deep(.p-datepicker table td) { padding: 2px; }
.quick-schedule__availability :deep(.p-datepicker table td > span) { width: 32px; height: 32px; }
.quick-schedule__day { display: flex; justify-content: center; align-items: center; width: 30px; height: 30px; border-radius: 50%; font-weight: 600; }
.quick-schedule__legend { display: flex; flex-wrap: wrap; justify-content: flex-start; gap: 3px; margin-top: 10px; }
.quick-schedule__availability select[size] { height: 290px; padding: 6px; }
.quick-schedule__availability select[size] option { padding: 7px 10px; border-radius: 5px; }
@media (max-width: 575px) {
  .quick-schedule__availability { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .quick-schedule__availability select[size] { height: 160px; }
}
.quick-schedule__empty { padding: 12px; background: #f7f5f9; border-radius: 8px; color: #706b79; font-size: 13px; }
.quick-schedule__summary { margin: 16px 0 0; color: #780f5a; font-weight: 600; font-size: 13px; }
.quick-schedule__success { text-align: center; padding: 16px 0; }
.quick-schedule__success > i { font-size: 40px; }
.quick-schedule__success h3 { margin: 16px 0; }
.quick-schedule__success small { color: #706b79; }
</style>
